// Import EmailJS
import emailjs from '@emailjs/browser';

// EmailJS Configuration from environment variables
const EMAILJS_CONFIG = {
    SERVICE_ID: import.meta.env.VITE_EMAILJS_SERVICE_ID,
    TEMPLATE_ID: import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
    PUBLIC_KEY: import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
    RECIPIENT_EMAIL: import.meta.env.VITE_EMAILJS_RECIPIENT_EMAIL || 'emprengi@efectemosquit.cat'
};

const API_BASE = String(import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');

// Initialize EmailJS
if (EMAILJS_CONFIG.PUBLIC_KEY) {
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
}

// --- Page Loading & Nav Generation (runs at module eval, before DOMContentLoaded) ---

const pageModules = import.meta.glob('./pages/**/*.html', { eager: true, query: '?raw', import: 'default' });

function parsePageMetadata(path, html) {
    const isFooter = path.includes('/footer/');
    const filename = path.split('/').pop().replace('.html', '');

    const orderMatch = html.match(/<!--\s*order:\s*(\d+)\s*-->/);
    const order = orderMatch ? parseInt(orderMatch[1]) : 999;

    const titleCommentMatch = html.match(/<!--\s*title:\s*(.+?)\s*-->/);
    let title = titleCommentMatch ? titleCommentMatch[1].trim() : null;

    if (!title) {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const h2 = temp.querySelector('h2');
        title = h2 ? h2.textContent.trim() : filename;
    }

    const sections = [];
    const temp = document.createElement('div');
    temp.innerHTML = html;
    temp.querySelectorAll('[id$="-section"].content-section').forEach(div => {
        const h3 = div.querySelector('h3');
        if (h3) {
            sections.push({ id: div.id.replace('-section', ''), label: h3.textContent.trim() });
        }
    });

    return { id: filename, title, order, sections, isFooter };
}

const pageMain = document.querySelector('.main-content');
const allPages = [];
const initialPageId = window.location.pathname === '/' ? 'home' : window.location.pathname.slice(1);

for (const [path, html] of Object.entries(pageModules)) {
    const page = parsePageMetadata(path, html);
    allPages.push(page);

    const section = document.createElement('section');
    section.id = page.id;
    section.className = page.id === initialPageId ? 'page active' : 'page';
    section.innerHTML = html;
    pageMain.appendChild(section);
}

if (!document.querySelector('.page.active')) {
    const homeSection = document.getElementById('home');
    if (homeSection) homeSection.classList.add('active');
}

(function generateNav() {
    const navList = document.getElementById('nav-list');
    if (!navList) return;

    allPages
        .filter(p => !p.isFooter && p.id !== 'home')
        .sort((a, b) => a.order - b.order)
        .forEach(page => {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.href = '/' + page.id;
            a.className = 'nav-link';
            a.setAttribute('data-page', page.id);
            a.textContent = page.title;

            if (page.sections.length >= 2) {
                li.className = 'nav-item dropdown';
                li.appendChild(a);
                const ul = document.createElement('ul');
                ul.className = 'dropdown-menu';
                page.sections.forEach(s => {
                    const sli = document.createElement('li');
                    const sa = document.createElement('a');
                    sa.href = '/' + page.id;
                    sa.setAttribute('data-section', s.id);
                    sa.textContent = s.label;
                    sli.appendChild(sa);
                    ul.appendChild(sli);
                });
                li.appendChild(ul);
            } else if (page.sections.length === 1) {
                li.className = 'nav-item';
                a.setAttribute('data-section', page.sections[0].id);
                li.appendChild(a);
            } else {
                li.className = 'nav-item';
                li.appendChild(a);
            }

            navList.appendChild(li);
        });
})();

(function generateFooterNav() {
    const footerNav = document.getElementById('footer-nav');
    if (!footerNav) return;

    const cookiesLink = footerNav.querySelector('#footer-cookies-link');

    allPages
        .filter(p => p.isFooter)
        .sort((a, b) => a.order - b.order)
        .forEach(page => {
            const link = document.createElement('a');
            link.href = '/' + page.id;
            link.className = 'footer-link';
            link.setAttribute('data-page', page.id);
            link.textContent = page.title;
            footerNav.insertBefore(link, cookiesLink);

            const sep = document.createElement('span');
            sep.className = 'footer-separator';
            sep.textContent = '|';
            footerNav.insertBefore(sep, cookiesLink);
        });
})();

// Scroll Animation Observer
function initScrollAnimations() {
    // Add the animate-ready class to body after a short delay to enable animations
    // This prevents FOUC (Flash of Unstyled Content)
    setTimeout(() => {
        document.body.classList.add('animate-ready');
        observeAnimations();
    }, 100);
}

function observeAnimations() {
    const animatedElements = document.querySelectorAll('.animate-on-scroll, .animate-fade-up, .animate-fade-left, .animate-fade-right, .animate-scale');
    
    if (animatedElements.length === 0) return;
    
    const observerOptions = {
        root: null,
        rootMargin: '50px 0px -30px 0px',
        threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('animate-visible');
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);
    
    animatedElements.forEach(el => {
        // If element is already in viewport, animate immediately with stagger
        const rect = el.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isInViewport) {
            el.classList.add('animate-visible');
        } else {
            observer.observe(el);
        }
    });
}

// Re-initialize animations when page changes
function reinitializePageAnimations() {
    const activePage = document.querySelector('.page.active');
    if (!activePage) return;
    
    const animatedElements = activePage.querySelectorAll('.animate-on-scroll, .animate-fade-up, .animate-fade-left, .animate-fade-right, .animate-scale');
    
    const observerOptions = {
        root: null,
        rootMargin: '50px 0px -30px 0px',
        threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('animate-visible');
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);
    
    animatedElements.forEach((el, index) => {
        // Reset visibility
        el.classList.remove('animate-visible');
        
        // Use requestAnimationFrame to ensure style is applied before checking
        requestAnimationFrame(() => {
            const rect = el.getBoundingClientRect();
            const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
            
            if (isInViewport) {
                // Stagger animations for elements in viewport
                setTimeout(() => {
                    el.classList.add('animate-visible');
                }, index * 80);
            } else {
                observer.observe(el);
            }
        });
    });
}

// Navigation and Page Management
document.addEventListener('DOMContentLoaded', function() {
    // Initialize scroll animations
    initScrollAnimations();
    // Get all page elements and navigation links
    const pages = document.querySelectorAll('.page');
    const navLinks = document.querySelectorAll('.nav-link[data-page]');
    const sectionLinks = document.querySelectorAll('[data-section]');
    const dropdowns = document.querySelectorAll('.dropdown');
    const mobileMenuToggle = document.getElementById('mobile-menu-toggle');
    const mainNav = document.getElementById('main-nav');
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    const header = document.querySelector('.header');
    const navLogo = document.getElementById('nav-logo');
    
    // Header scroll effect
    function handleScroll() {
        const headerHeight = header ? header.offsetHeight : 100;
        const scrollPosition = window.scrollY;
        
        if (scrollPosition > headerHeight - 20) {
            mainNav.classList.add('scrolled');
        } else {
            mainNav.classList.remove('scrolled');
        }
    }
    
    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    // Nav Logo click handler (same as header logo)
    if (navLogo) {
        navLogo.addEventListener('click', function(e) {
            e.preventDefault();
            switchPage('home');
            // Close mobile dropdowns
            dropdowns.forEach(dropdown => dropdown.classList.remove('active'));
            closeMobileMenu();
        });
    }
    
    // Helper function to close mobile menu
    function closeMobileMenu() {
        if (window.innerWidth <= 768) {
            mobileMenuToggle.classList.remove('active');
            mainNav.classList.remove('active');
            mobileMenuOverlay.classList.remove('active');
        }
    }
    
    // Helper function to open mobile menu
    function openMobileMenu() {
        if (window.innerWidth <= 768) {
            mobileMenuToggle.classList.add('active');
            mainNav.classList.add('active');
            mobileMenuOverlay.classList.add('active');
        }
    }

    // Function to pause YouTube video
    function pauseYoutubeVideo() {
        const iframe = document.querySelector('.youtube-video');
        if (iframe) {
            // Re-setting the src attribute stops the video
            const currentSrc = iframe.src;
            iframe.src = currentSrc;
        }
    }

    // Function to switch pages
    function switchPage(pageId) {
        if (!pageId) {
            console.warn('No pageId provided to switchPage');
            return;
        }
        
        // Pause video if moving away from page with video
        const activePage = document.querySelector('.page.active');
        if (activePage && activePage.id === 'emprengi' && pageId !== 'emprengi') {
            pauseYoutubeVideo();
        }
        
        // Hide all pages
        pages.forEach(page => page.classList.remove('active'));
        
        // Show the selected page
        const targetPage = document.getElementById(pageId);
        if (targetPage) {
            targetPage.classList.add('active');
            
            // Update URL without triggering navigation
            const newPath = pageId === 'home' ? '/' : '/' + pageId;
            if (window.location.pathname !== newPath) {
                window.history.pushState({ page: pageId }, '', newPath);
            }
            
            // Scroll to top of page
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            // Re-initialize animations for the new page
            setTimeout(() => {
                reinitializePageAnimations();
            }, 100);
        } else {
            console.warn(`Page with id "${pageId}" not found`);
        }
    }

    // Function to initialize page based on URL path
    function initializePage() {
        const path = window.location.pathname;
        let pageId = path.slice(1) || 'home'; // Remove leading slash, default to 'home'
        
        // Handle root path
        if (path === '/') {
            pageId = 'home';
        }
        
        if (document.getElementById(pageId)) {
            switchPage(pageId);
        } else {
            // Default to home page if invalid path
            switchPage('home');
        }
    }

    // Initialize page on load
    initializePage();

    // Listen for popstate changes (browser back/forward buttons)
    window.addEventListener('popstate', function(event) {
        const path = window.location.pathname;
        const pageId = path.slice(1) || 'home';
        if (document.getElementById(pageId)) {
            switchPage(pageId);
        }
    });

    // Function to scroll to a section within a page
    function scrollToSection(sectionId) {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            
            // Add a slight offset to account for sticky header
            setTimeout(() => {
                const headerOffset = 100;
                const elementPosition = section.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }, 100);
        }
    }

    // Add click handler for logo link (home button)
    const logoLink = document.getElementById('logo-link');
    if (logoLink) {
        logoLink.addEventListener('click', function(e) {
            e.preventDefault();
            switchPage('home');
            // Close mobile dropdowns
            dropdowns.forEach(dropdown => dropdown.classList.remove('active'));
        });
    }

    // Add click handlers for navigation links
    navLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            const isMobile = window.innerWidth <= 768;
            
            // Check if this link is inside a dropdown parent item
            const parentListItem = this.parentElement; // Should be <li class="nav-item dropdown">
            const isDropdownParent = parentListItem && parentListItem.classList.contains('dropdown');
            
            // On mobile, if clicking a dropdown parent link, toggle dropdown instead of navigating
            if (isMobile && isDropdownParent) {
                e.preventDefault();
                e.stopPropagation();
                parentListItem.classList.toggle('active');
                return; // Don't navigate or close menu
            }
            
            // Navigate to the page (desktop or non-dropdown items on mobile)
            e.preventDefault();
            const pageId = this.getAttribute('data-page');
            if (pageId) {
                switchPage(pageId);
            }
            
            // Close mobile dropdowns and menu when navigating
            dropdowns.forEach(dropdown => dropdown.classList.remove('active'));
            
            // Close mobile menu after navigating to a page
            closeMobileMenu();
        });
    });

    // Add click handlers for footer links
    const footerLinks = document.querySelectorAll('.footer-link[data-page]');
    footerLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            e.preventDefault();
            const pageId = this.getAttribute('data-page');
            if (pageId) {
                switchPage(pageId);
            }
            // Close mobile menu if open
            closeMobileMenu();
        });
    });

    // Add click handlers for section links (nav dropdowns, hero CTAs, cards with data-section, etc.)
    sectionLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            e.preventDefault();
            const dropdown = this.closest('.dropdown');
            let pageId = null;
            if (dropdown) {
                const navInDropdown = dropdown.querySelector('.nav-link[data-page]');
                pageId = navInDropdown ? navInDropdown.getAttribute('data-page') : null;
            } else {
                pageId = this.getAttribute('data-page');
                if (!pageId) {
                    const href = this.getAttribute('href') || '';
                    const m = href.replace(/^\.\//, '/').match(/^\/([^/?#]+)/);
                    if (m && document.getElementById(m[1])) {
                        pageId = m[1];
                    }
                }
            }
            const sectionId = this.getAttribute('data-section');

            if (pageId) {
                switchPage(pageId);
            }

            if (sectionId) {
                setTimeout(() => {
                    scrollToSection(`${sectionId}-section`);
                }, 300);
            }

            dropdowns.forEach(d => d.classList.remove('active'));
            closeMobileMenu();
        });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', function(e) {
        if (!e.target.closest('.dropdown')) {
            dropdowns.forEach(dropdown => dropdown.classList.remove('active'));
        }
    });

    // Delegated handler for [data-page] links inside page content (SPA navigation)
    document.querySelector('.main-content').addEventListener('click', function(e) {
        const link = e.target.closest('a[data-page]');
        if (!link) return;
        e.preventDefault();
        const pageId = link.getAttribute('data-page');
        const sectionId = link.getAttribute('data-section');
        if (pageId) switchPage(pageId);
        if (sectionId) {
            setTimeout(() => scrollToSection(sectionId + '-section'), 300);
        }
    });

    const contactForm = document.getElementById('contact-form');
    if (contactForm) {
        const nameInput = document.getElementById('name');
        const emailInput = document.getElementById('email');
        const messageInput = document.getElementById('message');
        const dataProtectionCheckbox = document.getElementById('data-protection');
        
        // Remove error class when user starts typing/editing
        function removeErrorClass(input) {
            input.addEventListener('input', function() {
                this.classList.remove('error');
            });
        }
        
        removeErrorClass(nameInput);
        removeErrorClass(emailInput);
        removeErrorClass(messageInput);
        
        dataProtectionCheckbox.addEventListener('change', function() {
            // Remove any visual error indication when checkbox is checked
            if (this.checked) {
                this.classList.remove('error');
                const checkboxGroup = this.closest('.form-group-checkbox');
                if (checkboxGroup) {
                    checkboxGroup.classList.remove('error');
                }
            }
        });
        
        contactForm.addEventListener('submit', function(e) {
            e.preventDefault();
            
            // Remove previous error classes
            nameInput.classList.remove('error');
            emailInput.classList.remove('error');
            messageInput.classList.remove('error');
            dataProtectionCheckbox.classList.remove('error');
            const checkboxGroup = dataProtectionCheckbox.closest('.form-group-checkbox');
            if (checkboxGroup) {
                checkboxGroup.classList.remove('error');
            }
            
            let hasErrors = false;
            
            // Validate required fields
            if (!nameInput.value.trim()) {
                nameInput.classList.add('error');
                hasErrors = true;
            }
            
            if (!emailInput.value.trim() || !emailInput.validity.valid) {
                emailInput.classList.add('error');
                hasErrors = true;
            }
            
            if (!messageInput.value.trim()) {
                messageInput.classList.add('error');
                hasErrors = true;
            }
            
            if (!dataProtectionCheckbox.checked) {
                dataProtectionCheckbox.classList.add('error');
                const checkboxGroup = dataProtectionCheckbox.closest('.form-group-checkbox');
                if (checkboxGroup) {
                    checkboxGroup.classList.add('error');
                }
                hasErrors = true;
            }
            
            if (hasErrors) {
                let errorMessage = 'Si us plau, omple tots els camps obligatoris.';
                if (!dataProtectionCheckbox.checked) {
                    errorMessage = 'Cal acceptar la política de protecció de dades per enviar el formulari.';
                }
                alert(errorMessage);
                return;
            }
            
            // Form is valid, submit via EmailJS
            const submitBtn = contactForm.querySelector('.submit-btn');
            const originalBtnText = submitBtn.textContent;
            
            // Disable button and show loading state
            submitBtn.disabled = true;
            submitBtn.textContent = 'Enviant...';
            
            // Check if EmailJS is configured
            if (!EMAILJS_CONFIG.SERVICE_ID || !EMAILJS_CONFIG.TEMPLATE_ID || !EMAILJS_CONFIG.PUBLIC_KEY ||
                EMAILJS_CONFIG.SERVICE_ID === 'your_service_id_here' || 
                EMAILJS_CONFIG.TEMPLATE_ID === 'your_template_id_here' || 
                EMAILJS_CONFIG.PUBLIC_KEY === 'your_public_key_here') {
                // EmailJS not configured, show fallback message
                submitBtn.textContent = originalBtnText;
                submitBtn.disabled = false;
                alert('Servei de contacte no disponible actualment');
                contactForm.reset();
                return;
            }
            
            // Prepare email template parameters
            const templateParams = {
                from_name: nameInput.value.trim(),
                from_email: emailInput.value.trim(),
                school: document.getElementById('school').value.trim() || 'No especificat',
                message: messageInput.value.trim(),
                to_email: EMAILJS_CONFIG.RECIPIENT_EMAIL,
            };
            
            // Send email via EmailJS
            emailjs.send(EMAILJS_CONFIG.SERVICE_ID, EMAILJS_CONFIG.TEMPLATE_ID, templateParams)
                .then(function(response) {
                    // Success
                    submitBtn.textContent = originalBtnText;
                    submitBtn.disabled = false;
                    alert('Gràcies per contactar-nos! El vostre missatge ha estat enviat correctament.');
                    contactForm.reset();
                }, function(error) {
                    // Error
                    submitBtn.textContent = originalBtnText;
                    submitBtn.disabled = false;
                    console.error('Error sending email:', error);
                    alert('Hi ha hagut un error en enviar el missatge. Si us plau, torneu-ho a intentar o contacteu-nos directament a ' + EMAILJS_CONFIG.RECIPIENT_EMAIL);
                });
        });
    }
    
    // Data Protection Popup
    const dataProtectionLink = document.getElementById('data-protection-link');
    const dataProtectionPopup = document.getElementById('data-protection-popup');
    const dataProtectionPopupClose = document.getElementById('data-protection-popup-close');
    
    // Condicions popup (contact form) — declare before data-protection handlers for mutual close
    const condicionsLink = document.getElementById('condicions-link');
    const condicionsPopup = document.getElementById('condicions-popup');
    const condicionsPopupClose = document.getElementById('condicions-popup-close');

    function hideCondicionsPopup() {
        if (!condicionsPopup) return;
        condicionsPopup.classList.remove('show');
        condicionsPopup.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    }

    function showCondicionsPopup() {
        if (!condicionsPopup) return;
        if (dataProtectionPopup) {
            dataProtectionPopup.classList.remove('show');
            dataProtectionPopup.setAttribute('aria-hidden', 'true');
        }
        condicionsPopup.classList.add('show');
        condicionsPopup.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
    }

    if (dataProtectionLink && dataProtectionPopup) {
        dataProtectionLink.addEventListener('click', function(e) {
            e.preventDefault();
            hideCondicionsPopup();
            dataProtectionPopup.classList.add('show');
            dataProtectionPopup.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
        });
    }
    
    if (dataProtectionPopupClose) {
        dataProtectionPopupClose.addEventListener('click', function() {
            dataProtectionPopup.classList.remove('show');
            dataProtectionPopup.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        });
    }
    
    if (dataProtectionPopup) {
        dataProtectionPopup.addEventListener('click', function(e) {
            if (e.target === dataProtectionPopup) {
                dataProtectionPopup.classList.remove('show');
                dataProtectionPopup.setAttribute('aria-hidden', 'true');
                document.body.style.overflow = '';
            }
        });
    }

    if (condicionsLink && condicionsPopup) {
        condicionsLink.addEventListener('click', function(e) {
            e.preventDefault();
            showCondicionsPopup();
        });
    }

    if (condicionsPopupClose) {
        condicionsPopupClose.addEventListener('click', function() {
            hideCondicionsPopup();
        });
    }

    if (condicionsPopup) {
        condicionsPopup.addEventListener('click', function(e) {
            if (e.target === condicionsPopup) {
                hideCondicionsPopup();
            }
        });
    }

    // Smooth scroll for all anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (href !== '#' && href !== '') {
                e.preventDefault();
                const target = document.querySelector(href);
                if (target) {
                    target.scrollIntoView({ behavior: 'smooth' });
                }
            }
        });
    });

    // Mobile Menu Toggle Functionality
    mobileMenuToggle.addEventListener('click', function() {
        if (mobileMenuToggle.classList.contains('active')) {
            closeMobileMenu();
        } else {
            openMobileMenu();
        }
    });
    
    // Close mobile menu when clicking on overlay
    mobileMenuOverlay.addEventListener('click', function() {
        closeMobileMenu();
    });
    
    // Scroll to Top Button Functionality
    const scrollToTopBtn = document.getElementById('scroll-to-top');
    
    // Show/hide scroll to top button based on scroll position
    window.addEventListener('scroll', function() {
        if (window.pageYOffset > 300) {
            scrollToTopBtn.classList.add('visible');
        } else {
            scrollToTopBtn.classList.remove('visible');
        }
    });
    
    // Scroll to top when button is clicked
    scrollToTopBtn.addEventListener('click', function() {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    });

    // Historia Carousel
    const historiaCarousel = document.getElementById('historia-carousel');
    if (historiaCarousel) {
        const carouselTrack = document.getElementById('carousel-track');
        const carouselIndicators = document.getElementById('carousel-indicators');
        const prevBtn = document.getElementById('carousel-prev');
        const nextBtn = document.getElementById('carousel-next');
        
        let currentSlide = 0;
        let autoSlideInterval = null;
        let images = [];
        let isInitialized = false;
        let touchStartX = 0;
        let touchEndX = 0;
        
        function initCarousel() {
            if (isInitialized) return;
            isInitialized = true;

            const slides = carouselTrack.querySelectorAll('.carousel-slide');
            if (slides.length === 0) {
                historiaCarousel.style.display = 'none';
                return;
            }

            images = Array.from(slides).map((slide, index) => {
                const img = slide.querySelector('img');
                return { path: img ? img.src : '', alt: img ? img.alt : `Slide ${index + 1}` };
            });

            carouselIndicators.innerHTML = '';
            images.forEach((_, index) => {
                const indicator = document.createElement('button');
                indicator.className = 'carousel-indicator';
                indicator.setAttribute('aria-label', `Imatge ${index + 1}`);
                indicator.setAttribute('data-slide', index);
                carouselIndicators.appendChild(indicator);
            });

            showSlide(0);
            startAutoSlide();
        }
        
        function showSlide(index) {
            if (images.length === 0) return;
            
            currentSlide = (index + images.length) % images.length;
            
            const indicators = carouselIndicators.querySelectorAll('.carousel-indicator');
            
            carouselTrack.style.transform = `translateX(-${currentSlide * 100}%)`;
            
            indicators.forEach((indicator, i) => {
                indicator.classList.toggle('active', i === currentSlide);
            });
        }
        
        function nextSlide() {
            showSlide(currentSlide + 1);
            resetAutoSlide();
        }
        
        function prevSlide() {
            showSlide(currentSlide - 1);
            resetAutoSlide();
        }
        
        function startAutoSlide() {
            autoSlideInterval = setInterval(() => showSlide(currentSlide + 1), 5000);
        }
        
        function stopAutoSlide() {
            if (autoSlideInterval) {
                clearInterval(autoSlideInterval);
                autoSlideInterval = null;
            }
        }
        
        function resetAutoSlide() {
            stopAutoSlide();
            startAutoSlide();
        }
        
        // Handle touch events for swipe
        function handleTouchStart(e) {
            touchStartX = e.changedTouches[0].screenX;
        }
        
        function handleTouchEnd(e) {
            touchEndX = e.changedTouches[0].screenX;
            handleSwipe();
        }
        
        function handleSwipe() {
            const swipeThreshold = 50;
            const diff = touchStartX - touchEndX;
            
            if (Math.abs(diff) > swipeThreshold) {
                if (diff > 0) {
                    nextSlide();
                } else {
                    prevSlide();
                }
            }
        }
        
        // Event listeners
        if (nextBtn) nextBtn.addEventListener('click', nextSlide);
        if (prevBtn) prevBtn.addEventListener('click', prevSlide);
        
        carouselIndicators.addEventListener('click', function(e) {
            if (e.target.classList.contains('carousel-indicator')) {
                const slideIndex = parseInt(e.target.getAttribute('data-slide'));
                showSlide(slideIndex);
                resetAutoSlide();
            }
        });
        
        // Touch events for mobile swipe
        historiaCarousel.addEventListener('touchstart', handleTouchStart, { passive: true });
        historiaCarousel.addEventListener('touchend', handleTouchEnd, { passive: true });
        
        // Pause on hover
        if (historiaCarousel) {
            historiaCarousel.addEventListener('mouseenter', stopAutoSlide);
            historiaCarousel.addEventListener('mouseleave', startAutoSlide);
        }
        
        // Initialize carousel
        initCarousel();
    }

    // Login Modal Management
    const loginBtn = document.getElementById('login-btn');
    const loginModal = document.getElementById('login-modal');
    const loginModalClose = document.getElementById('login-modal-close');
    const loginForm = document.getElementById('login-form');
    const emailInput = document.getElementById('login-email');
    const passwordInput = document.getElementById('login-password');
    const loginEmailError = document.getElementById('login-email-error');
    const loginPasswordError = document.getElementById('login-password-error');
    const passwordToggle = document.getElementById('login-password-toggle');

    function clearLoginErrors() {
        if (loginEmailError) loginEmailError.textContent = '';
        if (loginPasswordError) loginPasswordError.textContent = '';
        if (emailInput) emailInput.classList.remove('error');
        if (passwordInput) passwordInput.classList.remove('error');
    }

    function resetPasswordVisibility() {
        if (passwordInput && passwordInput.type !== 'password') {
            passwordInput.type = 'password';
        }
        if (passwordToggle) {
            passwordToggle.classList.remove('active');
            passwordToggle.setAttribute('aria-label', 'Mostra la contrasenya');
        }
    }

    function showLoginModal() {
        if (loginModal) {
            clearLoginErrors();
            resetPasswordVisibility();
            loginModal.classList.add('show');
            document.body.style.overflow = 'hidden';
        }
    }

    function hideLoginModal() {
        if (loginModal) {
            loginModal.classList.remove('show');
            document.body.style.overflow = '';
        }
    }

    if (loginBtn) {
        loginBtn.addEventListener('click', showLoginModal);
    }

    if (loginModalClose) {
        loginModalClose.addEventListener('click', hideLoginModal);
    }

    // Close modal when clicking outside
    if (loginModal) {
        loginModal.addEventListener('click', function(e) {
            if (e.target === loginModal) {
                hideLoginModal();
            }
        });
    }

    // Toggle password visibility
    if (passwordToggle && passwordInput) {
        passwordToggle.addEventListener('click', function() {
            const isCurrentlyHidden = passwordInput.type === 'password';
            passwordInput.type = isCurrentlyHidden ? 'text' : 'password';
            passwordToggle.classList.toggle('active', isCurrentlyHidden);
            passwordToggle.setAttribute('aria-label', isCurrentlyHidden ? 'Oculta la contrasenya' : 'Mostra la contrasenya');
        });
    }

    // Clear inline errors as user types
    if (emailInput) {
        emailInput.addEventListener('input', function() {
            emailInput.classList.remove('error');
            if (loginEmailError) loginEmailError.textContent = '';
        });
    }

    if (passwordInput) {
        passwordInput.addEventListener('input', function() {
            passwordInput.classList.remove('error');
            if (loginPasswordError) loginPasswordError.textContent = '';
        });
    }

    // Handle login form submission
    if (loginForm) {
        loginForm.addEventListener('submit', async function(e) {
            e.preventDefault();
            
            const submitBtn = loginForm.querySelector('.login-submit-btn');
            
            clearLoginErrors();

            if (!emailInput || !passwordInput) {
                alert('Formulari d\'inici de sessió no disponible. Torna-ho a intentar.');
                return;
            }

            const email = emailInput.value.trim();
            const password = passwordInput.value;
            
            // Basic validation
            if (!email || !password) {
                alert('Si us plau, introdueix el correu electrònic i la contrasenya.');
                return;
            }
            
            // Disable button and show loading state
            const originalBtnText = submitBtn.textContent;
            submitBtn.disabled = true;
            submitBtn.textContent = 'Entrant...';
            
            try {
                const response = await fetch(`${API_BASE}/login`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: email,
                        password: password
                    })
                });
                
                const data = await response.json();
                
                if (response.ok) {
                    // Success - store token and redirect to test page
                    localStorage.setItem('emprengi_auth_token', data.token);
                    clearLoginErrors();
                    hideLoginModal();
                    loginForm.reset();
                    resetPasswordVisibility();
                    window.location.href = '/escoles';
                } else {
                    // Handle error responses
                    let errorMessage = 'Error desconegut. Si us plau, torna-ho a intentar.';
                    
                    if (data.code === 'AUTH_MISSING_CREDENTIALS') {
                        errorMessage = 'Si us plau, introdueix el correu electrònic i la contrasenya.';
                    } else if (data.code === 'AUTH_INVALID_PASSWORD') {
                        errorMessage = 'La contrasenya és incorrecta.';
                    } else if (data.code === 'USER_NOT_FOUND') {
                        errorMessage = 'No s\'ha trobat cap usuari amb aquest correu electrònic.';
                    } else if (data.message) {
                        errorMessage = data.message;
                    }
                    
                    if (data.code === 'USER_NOT_FOUND') {
                        if (loginEmailError) {
                            loginEmailError.textContent = 'No existeix cap usuari amb aquest correu electrònic.';
                        }
                        if (emailInput) {
                            emailInput.classList.add('error');
                        }
                    } else if (data.code === 'AUTH_INVALID_PASSWORD') {
                        if (loginPasswordError) {
                            loginPasswordError.textContent = 'Contrasenya incorrecta.';
                        }
                        if (passwordInput) {
                            passwordInput.classList.add('error');
                        }
                    } else {
                        alert(errorMessage);
                    }
                }
            } catch (error) {
                console.error('Login error:', error);
                alert('Error de connexió. Si us plau, comprova la teva connexió a internet i torna-ho a intentar.');
            } finally {
                // Re-enable button
                submitBtn.disabled = false;
                submitBtn.textContent = originalBtnText;
            }
        });
    }

    // Close login modal on Escape key
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && loginModal && loginModal.classList.contains('show')) {
            hideLoginModal();
        }
    });

    // Cookie Consent Management
    const cookieBanner = document.getElementById('cookie-banner');
    const cookieModal = document.getElementById('cookie-modal');
    const cookieAccept = document.getElementById('cookie-accept');
    const cookieReject = document.getElementById('cookie-reject');
    const cookieSettings = document.getElementById('cookie-settings');
    const cookieModalClose = document.getElementById('cookie-modal-close');
    const cookieSave = document.getElementById('cookie-save');
    const cookieAcceptAll = document.getElementById('cookie-accept-all-modal');
    const cookieThirdParty = document.getElementById('cookie-third-party');

    // Cookie consent storage keys
    const COOKIE_CONSENT_KEY = 'emprengi_cookie_consent';
    const COOKIE_NECESSARY_KEY = 'emprengi_cookie_necessary';
    const COOKIE_THIRD_PARTY_KEY = 'emprengi_cookie_third_party';

    // Check if user has already given consent
    function hasCookieConsent() {
        return localStorage.getItem(COOKIE_CONSENT_KEY) !== null;
    }

    // Get cookie preferences
    function getCookiePreferences() {
        return {
            necessary: localStorage.getItem(COOKIE_NECESSARY_KEY) === 'true' || true, // Always true
            thirdParty: localStorage.getItem(COOKIE_THIRD_PARTY_KEY) === 'true'
        };
    }

    // Save cookie preferences
    function saveCookiePreferences(necessary, thirdParty) {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
        localStorage.setItem(COOKIE_NECESSARY_KEY, 'true'); // Always true
        localStorage.setItem(COOKIE_THIRD_PARTY_KEY, thirdParty ? 'true' : 'false');
    }

    // Show cookie banner
    function showCookieBanner() {
        if (cookieBanner && !hasCookieConsent()) {
            setTimeout(() => {
                cookieBanner.classList.add('show');
            }, 500); // Show after 500ms delay
        }
    }

    // Hide cookie banner
    function hideCookieBanner() {
        if (cookieBanner) {
            cookieBanner.classList.remove('show');
        }
    }

    // Show cookie modal
    function showCookieModal() {
        if (cookieModal) {
            const prefs = getCookiePreferences();
            cookieThirdParty.checked = prefs.thirdParty;
            cookieModal.classList.add('show');
            document.body.style.overflow = 'hidden';
        }
    }

    // Hide cookie modal
    function hideCookieModal() {
        if (cookieModal) {
            cookieModal.classList.remove('show');
            document.body.style.overflow = '';
        }
    }

    // Apply cookie preferences (load/unload third-party content)
    function applyCookiePreferences() {
        const prefs = getCookiePreferences();
        const youtubeIframe = document.getElementById('youtube-video');
        const youtubePlaceholder = document.getElementById('youtube-placeholder');
        
        if (!youtubeIframe || !youtubePlaceholder) {
            // Video container might not be on current page, that's okay
            return;
        }
        
        if (prefs.thirdParty) {
            // Load YouTube iframe
            const videoSrc = 'https://www.youtube-nocookie.com/embed/icV7qNmVm-0?start=1';
            
            // Hide placeholder
            youtubePlaceholder.style.display = 'none';
            
            // Set src (will trigger load if not already set)
            youtubeIframe.src = videoSrc;
            
            // Show iframe - use both style property and remove inline style
            youtubeIframe.removeAttribute('style');
            youtubeIframe.style.display = 'block';
        } else {
            // Hide YouTube and show placeholder
            youtubeIframe.style.display = 'none';
            youtubePlaceholder.style.display = 'flex';
            // Clear src to prevent loading
            youtubeIframe.src = '';
        }
    }

    // Accept all cookies
    function acceptAllCookies() {
        saveCookiePreferences(true, true);
        hideCookieBanner();
        hideCookieModal();
        applyCookiePreferences();
    }

    // Reject all non-necessary cookies
    function rejectAllCookies() {
        saveCookiePreferences(true, false);
        hideCookieBanner();
        hideCookieModal();
        applyCookiePreferences();
    }

    // Save preferences from modal
    function savePreferencesFromModal() {
        const thirdParty = cookieThirdParty.checked;
        saveCookiePreferences(true, thirdParty);
        hideCookieBanner();
        hideCookieModal();
        applyCookiePreferences();
    }

    // Event listeners
    if (cookieAccept) {
        cookieAccept.addEventListener('click', acceptAllCookies);
    }

    if (cookieReject) {
        cookieReject.addEventListener('click', rejectAllCookies);
    }

    if (cookieSettings) {
        cookieSettings.addEventListener('click', () => {
            hideCookieBanner();
            showCookieModal();
        });
    }

    if (cookieModalClose) {
        cookieModalClose.addEventListener('click', hideCookieModal);
    }

    if (cookieSave) {
        cookieSave.addEventListener('click', savePreferencesFromModal);
    }

    if (cookieAcceptAll) {
        cookieAcceptAll.addEventListener('click', acceptAllCookies);
    }

    // Close modal when clicking outside
    if (cookieModal) {
        cookieModal.addEventListener('click', (e) => {
            if (e.target === cookieModal) {
                hideCookieModal();
            }
        });
    }

    // Footer cookies link
    const footerCookiesLink = document.getElementById('footer-cookies-link');
    if (footerCookiesLink) {
        footerCookiesLink.addEventListener('click', function(e) {
            e.preventDefault();
            hideCookieBanner();
            showCookieModal();
        });
    }

    // Initialize cookie banner
    if (!hasCookieConsent()) {
        showCookieBanner();
    } else {
        // Apply existing preferences
        applyCookiePreferences();
    }

    // Gallery Management
    const galleryAnterior = document.getElementById('gallery-anterior');
    const galleryLightbox = document.getElementById('gallery-lightbox');
    const lightboxImage = document.getElementById('lightbox-image');
    const lightboxClose = document.getElementById('lightbox-close');
    const lightboxPrev = document.getElementById('lightbox-prev');
    const lightboxNext = document.getElementById('lightbox-next');
    const lightboxCounter = document.getElementById('lightbox-counter');

    let currentGalleryImages = [];
    let currentImageIndex = 0;

    function initGalleries() {
        if (galleryAnterior) {
            const items = galleryAnterior.querySelectorAll('.gallery-item');
            if (items.length === 0) return;

            const images = Array.from(items).map(item => {
                const img = item.querySelector('img');
                return { path: img ? img.src : '', alt: img ? img.alt : '' };
            });

            items.forEach((item, index) => {
                item.addEventListener('click', () => openLightbox(images, index));
            });
        }
    }

    // Open lightbox
    function openLightbox(images, index) {
        currentGalleryImages = images;
        currentImageIndex = index;
        showLightboxImage();
        galleryLightbox.classList.add('show');
        document.body.style.overflow = 'hidden';
    }

    // Show image in lightbox
    function showLightboxImage() {
        if (currentGalleryImages.length === 0) return;

        const image = currentGalleryImages[currentImageIndex];
        lightboxImage.src = image.path;
        lightboxImage.alt = image.alt;
        lightboxCounter.textContent = `${currentImageIndex + 1} / ${currentGalleryImages.length}`;

        // Enable/disable navigation buttons
        lightboxPrev.disabled = currentImageIndex === 0;
        lightboxNext.disabled = currentImageIndex === currentGalleryImages.length - 1;
    }

    // Close lightbox
    function closeLightbox() {
        galleryLightbox.classList.remove('show');
        document.body.style.overflow = '';
        currentGalleryImages = [];
        currentImageIndex = 0;
    }

    // Navigate lightbox
    function navigateLightbox(direction) {
        if (direction === 'next' && currentImageIndex < currentGalleryImages.length - 1) {
            currentImageIndex++;
        } else if (direction === 'prev' && currentImageIndex > 0) {
            currentImageIndex--;
        }
        showLightboxImage();
    }

    // Event listeners for lightbox
    if (lightboxClose) {
        lightboxClose.addEventListener('click', closeLightbox);
    }

    if (lightboxPrev) {
        lightboxPrev.addEventListener('click', () => navigateLightbox('prev'));
    }

    if (lightboxNext) {
        lightboxNext.addEventListener('click', () => navigateLightbox('next'));
    }

    // Close lightbox when clicking outside image
    if (galleryLightbox) {
        galleryLightbox.addEventListener('click', (e) => {
            if (e.target === galleryLightbox) {
                closeLightbox();
            }
        });
    }

    // Keyboard navigation for lightbox
    document.addEventListener('keydown', (e) => {
        if (!galleryLightbox.classList.contains('show')) return;

        switch (e.key) {
            case 'Escape':
                closeLightbox();
                break;
            case 'ArrowLeft':
                navigateLightbox('prev');
                break;
            case 'ArrowRight':
                navigateLightbox('next');
                break;
        }
    });

    // Initialize galleries
    initGalleries();

    // --- Cooperatives Public Listing ---
    const cooperativesListEl = document.getElementById('cooperatives-list');
    const loadMoreBtn = document.getElementById('cooperatives-load-more-btn');
    const cooperativesEmpty = document.getElementById('cooperatives-empty');

    let coopCurrentPage = 1;
    const coopPerPage = 10;
    let coopTotal = 0;
    let coopFiltersLoaded = false;

    const GRADE_LABELS = {
        'ESO_3': "3r d'ESO",
        'ESO_4': "4rt d'ESO",
        'BATX_1': "1r de batxillerat",
        'CFGM': "Cicle Formatiu de Grau Mitjà",
        'CFGS': "Cicle Formatiu de Grau Superior",
        'CFA': "Centre de Formació per Adults",
        'Alt': "Altres"
    };

    // Multi-select filter component
    const coopFilters = {};

    function createMultiSelect(containerId, options, onChange) {
        const container = document.getElementById(containerId);
        if (!container) return { getValues: () => [] };
        const placeholder = container.dataset.placeholder || 'Tots';
        let selected = [];

        container.innerHTML = `
            <div class="ms-display">
                <span class="ms-placeholder">${placeholder}</span>
                <span class="ms-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </span>
            </div>
            <div class="ms-tags"></div>
            <div class="ms-dropdown"></div>
        `;

        const display = container.querySelector('.ms-display');
        const tagsEl = container.querySelector('.ms-tags');
        const dropdown = container.querySelector('.ms-dropdown');

        options.forEach(opt => {
            const item = document.createElement('div');
            item.className = 'ms-option';
            item.dataset.value = opt.value;
            item.textContent = opt.label;
            item.addEventListener('click', (e) => {
                e.stopPropagation();
                toggleOption(opt.value, opt.label);
            });
            dropdown.appendChild(item);
        });

        function toggleOption(value, label) {
            const idx = selected.findIndex(s => s.value === value);
            if (idx >= 0) {
                selected.splice(idx, 1);
            } else {
                selected.push({ value, label });
            }
            render();
            onChange();
        }

        function render() {
            // Update dropdown active states
            dropdown.querySelectorAll('.ms-option').forEach(el => {
                el.classList.toggle('active', selected.some(s => s.value === el.dataset.value));
            });
            // Update tags
            tagsEl.innerHTML = '';
            if (selected.length === 0) {
                tagsEl.style.display = 'none';
                container.querySelector('.ms-placeholder').textContent = placeholder;
            } else {
                tagsEl.style.display = 'flex';
                container.querySelector('.ms-placeholder').textContent = `${selected.length} seleccionat${selected.length > 1 ? 's' : ''}`;
                selected.forEach(s => {
                    const tag = document.createElement('span');
                    tag.className = 'ms-tag';
                    tag.innerHTML = `${s.label}<button class="ms-tag-remove" aria-label="Treure">&times;</button>`;
                    tag.querySelector('.ms-tag-remove').addEventListener('click', (e) => {
                        e.stopPropagation();
                        toggleOption(s.value, s.label);
                    });
                    tagsEl.appendChild(tag);
                });
            }
        }

        display.addEventListener('click', (e) => {
            e.stopPropagation();
            // Close other open dropdowns
            document.querySelectorAll('.filter-multiselect.open').forEach(el => {
                if (el !== container) el.classList.remove('open');
            });
            container.classList.toggle('open');
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', () => {
            container.classList.remove('open');
        });
        container.addEventListener('click', (e) => e.stopPropagation());

        render();

        return {
            getValues: () => selected.map(s => s.value),
            clear: () => { selected = []; render(); }
        };
    }

    async function loadCooperativesFilters() {
        if (coopFiltersLoaded) return;
        try {
            const res = await fetch(`${API_BASE}/public/cooperatives/filters`);
            if (!res.ok) return;
            const data = await res.json();

            const onFilterChange = () => {
                coopCurrentPage = 1;
                loadCooperatives(false);
            };

            coopFilters.city = createMultiSelect('filter-city-ms',
                data.cities.map(c => ({ value: c.id, label: c.name })), onFilterChange);
            coopFilters.school = createMultiSelect('filter-school-ms',
                data.schools.map(s => ({ value: s.id, label: s.name })), onFilterChange);
            coopFilters.courseYear = createMultiSelect('filter-course-year-ms',
                data.course_years.map(y => ({ value: y, label: y })), onFilterChange);
            coopFilters.grade = createMultiSelect('filter-grade-ms',
                data.grades.map(g => ({ value: g.name, label: g.label })), onFilterChange);

            coopFiltersLoaded = true;
        } catch (err) {
            console.error('Error loading cooperatives filters:', err);
        }
    }

    async function loadCooperatives(append = false) {
        const params = new URLSearchParams({ page: coopCurrentPage, per_page: coopPerPage });
        const cityVals = coopFilters.city ? coopFilters.city.getValues() : [];
        const schoolVals = coopFilters.school ? coopFilters.school.getValues() : [];
        const yearVals = coopFilters.courseYear ? coopFilters.courseYear.getValues() : [];
        const gradeVals = coopFilters.grade ? coopFilters.grade.getValues() : [];

        if (cityVals.length) params.set('city', cityVals.join(','));
        if (schoolVals.length) params.set('school', schoolVals.join(','));
        if (yearVals.length) params.set('course_year', yearVals.join(','));
        if (gradeVals.length) params.set('grade', gradeVals.join(','));

        try {
            const res = await fetch(`${API_BASE}/public/cooperatives?${params}`);
            if (!res.ok) return;
            const data = await res.json();
            coopTotal = data.total;

            if (!append) {
                cooperativesListEl.innerHTML = '';
            }

            if (data.cooperatives.length === 0 && !append) {
                cooperativesEmpty.style.display = 'block';
                loadMoreBtn.style.display = 'none';
                return;
            }

            cooperativesEmpty.style.display = 'none';

            data.cooperatives.forEach(coop => {
                const card = createCooperativaCard(coop);
                cooperativesListEl.appendChild(card);
            });

            const loaded = cooperativesListEl.children.length;
            loadMoreBtn.style.display = loaded < coopTotal ? 'inline-block' : 'none';
        } catch (err) {
            console.error('Error loading cooperatives:', err);
        }
    }

    function createCooperativaCard(coop) {
        const card = document.createElement('div');
        card.className = 'coop-card';
        card.setAttribute('data-id', coop.id);

        const gradeLabel = GRADE_LABELS[coop.grade] || coop.grade;
        const schoolName = coop.school ? coop.school.name : '';
        const cityName = coop.city ? coop.city.name : '';

        card.innerHTML = `
            <div class="coop-card-header">
                <div class="coop-card-logo">
                    ${coop.logo_url
                        ? `<img src="${coop.logo_url}" alt="${coop.name}" />`
                        : `<div class="coop-card-logo-placeholder">${coop.name.charAt(0)}</div>`}
                </div>
                <div class="coop-card-info">
                    <h3 class="coop-card-name">${coop.name}</h3>
                    <div class="coop-card-meta">
                        ${schoolName ? `<span class="coop-meta-item">${schoolName}</span>` : ''}
                        ${cityName ? `<span class="coop-meta-item">${cityName}</span>` : ''}
                        <span class="coop-meta-item">${coop.course_year}</span>
                        <span class="coop-meta-item">${gradeLabel}</span>
                    </div>
                </div>
                <button class="coop-card-toggle" aria-label="Expandir">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>
            </div>
            <div class="coop-card-body">
                <div class="coop-card-description">${coop.description}</div>
            </div>
        `;

        const toggle = card.querySelector('.coop-card-toggle');
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            card.classList.toggle('expanded');
        });
        card.querySelector('.coop-card-header').addEventListener('click', () => {
            card.classList.toggle('expanded');
        });

        return card;
    }


    // Load more button
    loadMoreBtn.addEventListener('click', () => {
        coopCurrentPage++;
        loadCooperatives(true);
    });

    // Load filters and initial data when cooperatives page becomes active
    const coopPageObserver = new MutationObserver(() => {
        const coopPage = document.getElementById('cooperatives');
        if (coopPage && coopPage.classList.contains('active')) {
            loadCooperativesFilters();
            if (cooperativesListEl.children.length === 0) {
                loadCooperatives(false);
            }
        }
    });
    const coopPage = document.getElementById('cooperatives');
    if (coopPage) {
        coopPageObserver.observe(coopPage, { attributes: true, attributeFilter: ['class'] });
        if (coopPage.classList.contains('active')) {
            loadCooperativesFilters();
            loadCooperatives(false);
        }
    }
});
